import type { RaceLaneResult } from '@/types';

/**
 * The rules that decide what a race-data correction actually contains.
 *
 * Extracted from the edit dialog so it can be tested directly. Race results are
 * official records: the dangerous outcome here is not a crash but a save that
 * reports success having corrected nothing, or one that sends a field name the
 * server does not recognise (it rejects the whole payload with a 400 naming the
 * offenders — see server/models/RaceDataCorrection.js).
 */

/**
 * camelCase API field -> snake_case column, matching the keys the server
 * returns on the *original* record. Keys absent here are identical in both.
 * Must stay in step with CORRECTION_FIELD_MAP on the server.
 */
export const CAMEL_TO_SNAKE: Record<string, string> = {
  carNo: 'car_no', driver: 'driver', carClass: 'car_class',
  rt: 'rt', rtStr: 'rt_str',
  ft60: 'ft60', ft60Str: 'ft60str',
  ft330: 'ft330', ft330Str: 'ft330str',
  ft8: 'ft8', ft8Str: 'ft8str',
  ft8Mph: 'ft8mph', ft8MphStr: 'ft8mph_str',
  ft1000: 'ft1000', ft1000Str: 'ft1000str',
  ft4: 'ft4', ft4Str: 'ft4str',
  ft4Mph: 'ft4mph', ft4MphStr: 'ft4mph_str',
  dialIn: 'dial_in', dialInStr: 'dial_in_str',
  first: 'first', firstStr: 'first_str',
  win: 'win', mov: 'mov', lane: 'lane',
  member: 'member', bumpSpot: 'bump_spot',
  qPos: 'q_pos', qPosStr: 'q_pos_str',
};

export interface EditableField {
  key: keyof RaceLaneResult;
  label: string;
}

/** The correctable field set. Mirrors CORRECTION_FIELD_MAP's keys. */
export const EDITABLE_FIELDS: EditableField[] = [
  { key: 'carNo', label: 'Car No' },
  { key: 'driver', label: 'Driver' },
  { key: 'carClass', label: 'Car Class' },
  { key: 'rt', label: 'RT' },
  { key: 'rtStr', label: 'RT Str' },
  { key: 'ft60', label: '60 ft' },
  { key: 'ft60Str', label: '60 ft Str' },
  { key: 'ft330', label: '330 ft' },
  { key: 'ft330Str', label: '330 ft Str' },
  { key: 'ft8', label: '1/8 mi' },
  { key: 'ft8Str', label: '1/8 mi Str' },
  { key: 'ft8Mph', label: '1/8 MPH' },
  { key: 'ft8MphStr', label: '1/8 MPH Str' },
  { key: 'ft1000', label: '1000 ft' },
  { key: 'ft1000Str', label: '1000 ft Str' },
  { key: 'ft4', label: '1/4 mi' },
  { key: 'ft4Str', label: '1/4 mi Str' },
  { key: 'ft4Mph', label: '1/4 MPH' },
  { key: 'ft4MphStr', label: '1/4 MPH Str' },
  { key: 'dialIn', label: 'Dial-In' },
  { key: 'dialInStr', label: 'Dial-In Str' },
  { key: 'bumpSpot', label: 'Bump Spot' },
  { key: 'win', label: 'Win' },
  { key: 'first', label: 'First' },
  { key: 'firstStr', label: 'First Str' },
  { key: 'mov', label: 'MOV' },
  { key: 'qPos', label: 'QPos' },
  { key: 'qPosStr', label: 'QPos Str' },
];

/** Fields parsed as numbers rather than kept as text. */
export const NUMERIC_FIELDS: string[] = [
  'rt', 'ft60', 'ft330', 'ft8', 'ft8Mph', 'ft1000', 'ft4', 'ft4Mph', 'dialIn', 'mov', 'qPos',
];

export type Baseline = Record<string, unknown> | null | undefined;

/** Parse a raw input value for `field`. Blank numerics become null, not NaN. */
export function parseFieldValue(field: string, raw: string): string | number | null {
  if (!NUMERIC_FIELDS.includes(field)) return raw;
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

/** The baseline value for `field`, keyed by its snake_case column. */
export function baselineValueOf(baseline: Baseline, field: string): unknown {
  if (!baseline) return undefined;
  return baseline[CAMEL_TO_SNAKE[field] ?? field];
}

/** Baseline value formatted for display; an em dash when there is none. */
export function displayBaselineValue(baseline: Baseline, field: string): string | undefined {
  if (!baseline) return undefined;
  const value = baselineValueOf(baseline, field);
  return value === null || value === undefined ? '—' : String(value);
}

/**
 * Project the row being edited into the snake_case shape a baseline must have.
 *
 * This is the fallback when the server's original record could not be fetched.
 * It has to be a *complete* projection: `baselineValueOf` looks values up by
 * snake_case key, so handing it the camelCase row directly would return
 * `undefined` for every field, mark all of them changed, and turn a one-field
 * correction into a full overwrite of the record.
 */
export function rowAsBaseline(row: Partial<RaceLaneResult> | null): Baseline {
  if (!row) return null;
  const baseline: Record<string, unknown> = {};
  for (const { key } of EDITABLE_FIELDS) {
    const column = CAMEL_TO_SNAKE[key as string] ?? (key as string);
    baseline[column] = row[key] ?? null;
  }
  return baseline;
}

/**
 * Blank, null and undefined are all "no value" and compare equal.
 *
 * Numeric fields are compared by numeric value rather than by text, so a
 * baseline of `9.88` and a freshly typed `9.880` are the same reading and do
 * not register as a correction.
 */
const normalise = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (NUMERIC_FIELDS.includes(field)) {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? String(value) : String(parsed);
  }
  return String(value);
};

/**
 * The value to send for `field`, given what is currently in the input.
 *
 * Numeric fields are held as raw text while the user types — parsing on every
 * keystroke makes decimals impossible to enter, because `Number('0.')` is `0`
 * and the decimal point is swallowed the moment it is typed. Parsing is
 * deferred to here.
 */
export function committedValue(edited: Partial<RaceLaneResult>, field: string): unknown {
  const raw = edited[field as keyof RaceLaneResult];
  if (raw === '' || raw === undefined || raw === null) return null;
  return NUMERIC_FIELDS.includes(field) ? parseFieldValue(field, String(raw)) : raw;
}

/** Has `field` been edited away from the baseline? */
export function isFieldChanged(
  edited: Partial<RaceLaneResult>,
  baseline: Baseline,
  field: string
): boolean {
  if (!baseline) return false;
  return (
    normalise(field, edited[field as keyof RaceLaneResult]) !==
    normalise(field, baselineValueOf(baseline, field))
  );
}

/**
 * The correction payload: only the fields that differ from the baseline, with
 * cleared values sent as explicit nulls.
 *
 * `baseline` is the server's original record when it loaded, and otherwise the
 * row being edited. That fallback matters: comparing against `null` makes every
 * field look unchanged, so the payload comes out empty and the save is refused
 * with "No fields have been changed" — even though the user did change
 * something. Callers must therefore always pass a usable baseline.
 */
export function computeChangedFields(
  edited: Partial<RaceLaneResult>,
  baseline: Baseline
): Record<string, unknown> {
  const changed: Record<string, unknown> = {};
  for (const { key } of EDITABLE_FIELDS) {
    if (!isFieldChanged(edited, baseline, key as string)) continue;
    changed[key as string] = committedValue(edited, key as string);
  }
  return changed;
}
